import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { emergencyFuseApi } from '../api';
import type { EmergencyFusePayload, EmergencyFuseState } from '../types';
import { getDefaultFuseState, getErrorMessage } from '../utils';

export function useEmergencyFuse() {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<EmergencyFuseState>(getDefaultFuseState());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchState = useCallback(async () => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const result = await emergencyFuseApi.getState(controller.signal);

      if (!mountedRef.current) return;

      setState(result);
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const updateState = useCallback(async (payload: EmergencyFusePayload) => {
    setSaving(true);

    try {
      return await emergencyFuseApi.updateState(payload);
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    fetchState();

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [fetchState]);

  return useMemo(
    () => ({
      state,
      loading,
      saving,
      error,
      refetch: fetchState,
      updateState,
    }),
    [error, fetchState, loading, saving, state, updateState]
  );
}
